import { supabase } from "@/shared/lib/supabaseClient";
import type { EventoVisibilidade } from "@/shared/types/database.types";

/**
 * Agregador de leitura do Calendário Central (Sprint 3). Junta, num período,
 * os Eventos, as Reservas (que não são de eventos, para não duplicar) e as
 * devoluções previstas da Biblioteca. Cada consulta respeita a RLS do seu
 * módulo — o que a pessoa não pode ver, simplesmente não vem. Falha isolada
 * de um módulo não derruba o calendário inteiro.
 *
 * Sprint 3 (22/07/2026): cada item agora carrega `refId` (id cru da entidade),
 * `ministerioId` e `status`, para permitir editar/cancelar direto no
 * Calendário (reaproveitando as telas de Eventos/Reservas).
 *
 * Fase 3 (27/09/2026): Escala é a 4ª origem, integrada sem duplicar suas
 * regras (arquitetura §13) — a RLS de `escalas` já resolve visibilidade
 * (dono/visão ampla/participação), então a consulta abaixo só filtra
 * `situacao='ativa' AND data_hora` dentro do período; nunca mostra escala
 * cancelada nem passada. Autorização de editar/cancelar é POR MINISTÉRIO
 * (não um `podeGerir` genérico da tela) — por isso Escala é somente leitura
 * em `DetalheItemCalendario`, com link para a tela real de Escala (ver
 * comentário lá).
 */
export type CalendarioModulo = "evento" | "reserva" | "biblioteca" | "escala";

export interface ItemCalendario {
  id: string; // chave única de UI (com prefixo do módulo)
  refId: string; // id cru da entidade, para abrir no módulo
  modulo: CalendarioModulo;
  titulo: string;
  inicio: string; // ISO
  fim: string | null; // ISO
  diaInteiro: boolean;
  local: string | null;
  ministerio: string | null;
  ministerioId: string | null;
  status: string | null;
  visibilidade: EventoVisibilidade | null;
  /**
   * Texto livre que a pessoa escreveu no item: "Descrição" (Evento e Escala) ou
   * "Observações" (Reserva). `null` quando vazio e na Biblioteca (devolução prevista).
   */
  observacao: string | null;
}

interface LinhaEvento {
  id: string;
  titulo: string;
  descricao: string | null;
  inicio: string;
  fim: string;
  dia_inteiro: boolean;
  visibilidade: EventoVisibilidade;
  ministerio_id: string | null;
  status: string;
  ambientes: { nome: string } | null;
  ministerios: { nome: string } | null;
}
interface LinhaReserva {
  id: string;
  titulo: string;
  observacoes: string | null;
  inicio: string;
  fim: string;
  ministerio_id: string | null;
  status: string;
  ambientes: { nome: string } | null;
  ministerios: { nome: string } | null;
}
interface LinhaEmprestimo {
  id: string;
  data_devolucao_prevista: string | null;
  livros: { nome_livro: string } | null;
}
interface LinhaEscala {
  id: string;
  nome: string;
  descricao: string | null;
  data_hora: string;
  ministerio_id: string;
  situacao: string;
  ministerios: { nome: string } | null;
}

export const calendarioService = {
  async listarPeriodo(inicioISO: string, fimISO: string): Promise<ItemCalendario[]> {
    const dataInicio = inicioISO.slice(0, 10);
    const dataFim = fimISO.slice(0, 10);

    const [eventosRes, reservasRes, emprestimosRes, escalasRes] = await Promise.all([
      supabase
        .from("eventos")
        .select("id, titulo, descricao, inicio, fim, dia_inteiro, visibilidade, ministerio_id, status, ambientes(nome), ministerios!eventos_ministerio_id_fkey(nome)")
        .is("deleted_at", null)
        .eq("status", "agendado")
        // F4-17 (revisão 21/07/2026): evento pode durar mais de um dia — usa
        // sobreposição de intervalo (início <= fim da janela E fim >= início
        // da janela) em vez de só olhar o início. Com a checagem antiga
        // (`inicio` dentro da janela), um evento de vários dias que começou
        // ANTES da janela visível mas ainda está em andamento durante ela
        // sumia da tela.
        .lte("inicio", fimISO)
        .gte("fim", inicioISO),
      supabase
        .from("reservas")
        .select("id, titulo, observacoes, inicio, fim, ministerio_id, status, ambientes(nome), ministerios!reservas_ministerio_id_fkey(nome)")
        .is("evento_id", null)
        .eq("status", "confirmada")
        .gte("inicio", inicioISO)
        .lte("inicio", fimISO),
      supabase
        .from("emprestimos")
        .select("id, data_devolucao_prevista, livros(nome_livro)")
        .in("status", ["EM_ABERTO", "ATRASADO"])
        .not("data_devolucao_prevista", "is", null)
        .gte("data_devolucao_prevista", dataInicio)
        .lte("data_devolucao_prevista", dataFim),
      supabase
        .from("escalas")
        .select("id, nome, descricao, data_hora, ministerio_id, situacao, ministerios(nome)")
        .eq("situacao", "ativa")
        .gte("data_hora", inicioISO)
        .lte("data_hora", fimISO),
    ]);

    const itens: ItemCalendario[] = [];

    if (!eventosRes.error) {
      const linhas = (eventosRes.data ?? []) as unknown as LinhaEvento[];
      for (const e of linhas) {
        itens.push({
          id: `evento-${e.id}`,
          refId: e.id,
          modulo: "evento",
          titulo: e.titulo,
          inicio: e.inicio,
          fim: e.fim,
          diaInteiro: e.dia_inteiro,
          local: e.ambientes?.nome ?? null,
          ministerio: e.ministerios?.nome ?? null,
          ministerioId: e.ministerio_id ?? null,
          status: e.status,
          visibilidade: e.visibilidade,
          observacao: e.descricao?.trim() || null,
        });
      }
    }

    if (!reservasRes.error) {
      const linhas = (reservasRes.data ?? []) as unknown as LinhaReserva[];
      for (const r of linhas) {
        itens.push({
          id: `reserva-${r.id}`,
          refId: r.id,
          modulo: "reserva",
          titulo: r.titulo,
          inicio: r.inicio,
          fim: r.fim,
          diaInteiro: false,
          local: r.ambientes?.nome ?? null,
          ministerio: r.ministerios?.nome ?? null,
          ministerioId: r.ministerio_id ?? null,
          status: r.status,
          visibilidade: null,
          observacao: r.observacoes?.trim() || null,
        });
      }
    }

    if (!emprestimosRes.error) {
      const linhas = (emprestimosRes.data ?? []) as unknown as LinhaEmprestimo[];
      for (const emp of linhas) {
        if (!emp.data_devolucao_prevista) continue;
        itens.push({
          id: `emprestimo-${emp.id}`,
          refId: emp.id,
          modulo: "biblioteca",
          titulo: `Devolução: ${emp.livros?.nome_livro ?? "livro"}`,
          inicio: `${emp.data_devolucao_prevista}T00:00:00`,
          fim: null,
          diaInteiro: true,
          local: "Biblioteca",
          ministerio: null,
          ministerioId: null,
          status: null,
          visibilidade: null,
          observacao: null,
        });
      }
    }

    if (!escalasRes.error) {
      const linhas = (escalasRes.data ?? []) as unknown as LinhaEscala[];
      for (const e of linhas) {
        itens.push({
          id: `escala-${e.id}`,
          refId: e.id,
          modulo: "escala",
          titulo: e.nome,
          inicio: e.data_hora,
          fim: null,
          diaInteiro: false,
          local: null,
          ministerio: e.ministerios?.nome ?? null,
          ministerioId: e.ministerio_id,
          status: e.situacao,
          visibilidade: null,
          observacao: e.descricao?.trim() || null,
        });
      }
    }

    return itens.sort((a, b) => a.inicio.localeCompare(b.inicio));
  },
};
